export const PRINT_DOCUMENT_TYPES = Object.freeze({
  RECEIPT: "receipt",
  OPEN_BILL: "open_bill",
  INVOICE: "invoice",
  TEST: "test",
  KITCHEN_TICKET: "kitchen_ticket",
});

export const PRINT_ADAPTERS = Object.freeze({
  BROWSER: "browser",
  QZ: "qz",
  ANDROID_BRIDGE: "android_bridge",
  CLEANter: "cleanter",
  TCP: "tcp",
  CLOUDPRNT: "cloudprnt",
});

export const PRINT_PROTOCOLS = Object.freeze({
  ESC_POS: "escpos",
  STAR: "star",
  ZPL: "zpl",
  NONE: "none",
});

export const PRINT_CONNECTIONS = Object.freeze({
  BROWSER: "browser",
  QZ_DESKTOP: "qz_desktop",
  BLUETOOTH_ANDROID: "bluetooth_android",
  CLEANter: "cleanter",
  TCP: "tcp",
  CLOUDPRNT: "cloudprnt",
});

export const PRINT_ERROR_CODES = Object.freeze({
  PRINTER_NOT_CONFIGURED: "PRINTER_NOT_CONFIGURED",
  ADAPTER_UNAVAILABLE: "ADAPTER_UNAVAILABLE",
  BRIDGE_NOT_RUNNING: "BRIDGE_NOT_RUNNING",
  PRINTER_NOT_FOUND: "PRINTER_NOT_FOUND",
  PAIRING_REQUIRED: "PAIRING_REQUIRED",
  CONNECTION_FAILED: "CONNECTION_FAILED",
  WRITE_FAILED: "WRITE_FAILED",
  TIMEOUT: "TIMEOUT",
  UNSUPPORTED_PROTOCOL: "UNSUPPORTED_PROTOCOL",
  UNSUPPORTED_CAPABILITY: "UNSUPPORTED_CAPABILITY",
  ENCODING_ERROR: "ENCODING_ERROR",
  PRINT_JOB_REJECTED: "PRINT_JOB_REJECTED",
});

function createId(prefix = "print") {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return `${prefix}_${crypto.randomUUID()}`;
    }
  } catch {
    // Fall through to the timestamp fallback.
  }

  return `${prefix}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

export function normalizePrinterProfile(printer = {}, adapter = "browser") {
  const normalizedAdapter = printer?.adapter || adapter || "browser";

  return {
    adapter: normalizedAdapter,
    name: printer?.name || "",
    manufacturer: printer?.manufacturer || "",
    model: printer?.model || "",
    connection: printer?.connection || "",
    protocol: printer?.protocol || PRINT_PROTOCOLS.NONE,
    paper_width: String(printer?.paper_width || "80"),
    bridge_url: printer?.bridge_url || "",
    bridge_token: printer?.bridge_token || "",
    printer_id: printer?.printer_id || "",
    print_width_dots: printer?.print_width_dots || null,
    encoding: printer?.encoding || "",
    capabilities: {
      text: printer?.capabilities?.text !== false,
      image: Boolean(printer?.capabilities?.image),
      qr: Boolean(printer?.capabilities?.qr),
      barcode: Boolean(printer?.capabilities?.barcode),
      cut: Boolean(printer?.capabilities?.cut),
      cash_drawer: Boolean(printer?.capabilities?.cash_drawer),
      status: Boolean(printer?.capabilities?.status),
    },
    auto_print: Boolean(printer?.auto_print),
  };
}

export function createPrintJob({
  document = null,
  printer = {},
  adapter = "browser",
  copies = 1,
} = {}) {
  if (!document || typeof document !== "object") {
    throw new Error("Print document belum tersedia.");
  }

  const normalizedPrinter = normalizePrinterProfile(printer, adapter);

  return {
    job_id: createId("gloo_print"),
    document_type: document.type || PRINT_DOCUMENT_TYPES.RECEIPT,
    document_version: document.version || 1,
    created_at: new Date().toISOString(),
    copies: Math.max(1, Number(copies) || 1),
    source: {
      order_id: document?.data?.id || document?.data?._id || null,
      transaction_number:
        document?.data?.transaction_number ||
        document?.data?.transactionNumber ||
        null,
    },
    content: document.data ?? null,
    printer: normalizedPrinter,
  };
}

export function createPrintResult({
  job,
  success = false,
  adapter = "",
  ...extra
} = {}) {
  return {
    success: Boolean(success),
    job_id: job?.job_id || null,
    adapter: adapter || job?.printer?.adapter || "",
    document_type: job?.document_type || null,
    ...extra,
  };
}

export function createPrintError(
  code,
  message,
  {
    job = null,
    adapter = "",
    cause = null,
  } = {}
) {
  const error = new Error(message);

  error.code = code;
  error.job_id = job?.job_id || null;
  error.adapter = adapter || job?.printer?.adapter || "";
  error.cause = cause || null;

  return error;
}
